import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ArrowRight, Star, CheckCircle, Brain, Menu, X, MessageCircle, BarChart3, Users, Sparkles, TrendingUp, MessageSquare, LineChart, Lightbulb } from '../../components/icons';
import { Linkedin, Twitter, Instagram, Facebook } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';

function HeaderTR() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { label: 'Ana Sayfa', href: '/tr' },
    { label: 'Hakkında', href: '#about' },
    { label: 'Özellikler', href: '#features' },
    { label: 'SSS', href: '#faq' },
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    if (href.startsWith('#')) {
      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(href);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-lg shadow-shadow-md' : 'bg-transparent'}`}>
      <div className="max-w-[1216px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          <button onClick={() => navigate('/tr')} className="flex items-center gap-2">
            <img src="/human_potential_os_logo_V2_cropped.png" alt="NLP OS" className="h-14" />
          </button>
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button key={link.label} onClick={() => handleNavClick(link.href)} className="text-md font-semibold text-gray-60 hover:text-brand-60 transition-colors">
                {link.label}
              </button>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/tr/login')} className="text-gray-60">Giriş Yap</Button>
            <Button onClick={() => navigate('/tr/register')}>Başla</Button>
          </div>
          <button className="md:hidden p-2 text-gray-60" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-20">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <button key={link.label} onClick={() => handleNavClick(link.href)} className="block w-full text-left px-4 py-3 text-md font-semibold text-gray-60 hover:bg-gray-10 rounded-xl transition-colors">
                {link.label}
              </button>
            ))}
            <div className="pt-4 space-y-2">
              <Button variant="outline" className="w-full" onClick={() => { setIsMobileMenuOpen(false); navigate('/tr/login'); }}>Giriş Yap</Button>
              <Button className="w-full" onClick={() => { setIsMobileMenuOpen(false); navigate('/tr/register'); }}>Başla</Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function HeroSectionTR() {
  const navigate = useNavigate();
  return (
    <section className="pt-28 pb-16 md:pt-32 md:pb-24 px-4 md:px-8 bg-brand-5">
      <div className="max-w-[1216px] mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          <div className="flex-1 relative">
            <div className="relative max-w-md mx-auto lg:mx-0">
              <div className="absolute -top-4 -left-4 w-full h-full bg-success-10 rounded-[32px] transform rotate-3" />
              <div className="relative bg-white rounded-[32px] shadow-soft-lg overflow-hidden">
                <div className="p-6 border-b border-gray-20">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-brand-10 rounded-full flex items-center justify-center">
                      <span className="text-2xl">🎯</span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-40">Değerlendirme</p>
                      <p className="font-semibold text-gray-80">Kendinizi nasıl tanımlarsınız?</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 h-2 bg-success-40 rounded-full" />
                    <div className="flex-1 h-2 bg-success-10 rounded-full" />
                    <div className="flex-1 h-2 bg-success-10 rounded-full" />
                  </div>
                </div>
                <div className="p-6 space-y-3">
                  {['Yeteneklerime güveniyorum.', 'Yeni zorlukları kabul ederim.', 'Başkalarıyla kolayca bağ kurarım.'].map((text, i) => (
                    <div key={i} className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${i === 0 ? 'border-success-40 bg-success-5' : 'border-gray-20 hover:border-gray-30'}`}>
                      <CheckCircle className={`w-5 h-5 ${i === 0 ? 'text-success-40' : 'text-gray-30'}`} />
                      <span className="text-gray-60 text-sm">{text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-soft p-4 hidden md:block">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-orange-10 rounded-full flex items-center justify-center">
                    <span className="text-lg">✨</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-40">İçgörüler</p>
                    <p className="text-sm font-semibold text-gray-80">Yapay Zekâ Analizi</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 text-center lg:text-left">
            <Badge variant="brand" className="mb-6">
              <Star className="w-4 h-4 mr-1 fill-brand-40 text-brand-40" />
              Kişilik Değerlendirmesi
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-heading-xl font-bold text-brand-90 mb-6 leading-tight">
              Gerçek Potansiyelinizi Anlayın.{' '}
              <span className="text-success-40">Her Zaman, Her Yerde.</span>
            </h1>
            <p className="text-lg md:text-paragraph-xl text-gray-60 mb-8 max-w-xl mx-auto lg:mx-0">
              Kişilik değerlendirmeleri yalnızca büyük dönüm noktalarında değil, günlük netlikte de yol gösterir. NLP OS™, bütünlük, gelişim ve kariyer başarısı yolculuğunuzda yanınızdadır. Kişisel potansiyel yolculuğunuzu anlamanıza, yönetmenize ve geliştirmenize yardımcı olmak için tasarlanmıştır.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button size="lg" onClick={() => navigate('/tr/register')} className="gap-2">
                Değerlendirmeye Başla
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Button variant="secondary" size="lg" onClick={() => navigate('/example')}>
                Örnek Sonuçları Görüntüle
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBarTR() {
  const avatars = [
    'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=100',
    'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=100',
    'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=100',
    'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=100',
    'https://images.pexels.com/photos/1681010/pexels-photo-1681010.jpeg?auto=compress&cs=tinysrgb&w=100',
  ];
  return (
    <section className="py-6 px-4 md:px-8 bg-brand-40">
      <div className="max-w-[1216px] mx-auto flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
        <div className="flex -space-x-3">
          {avatars.map((avatar, i) => (
            <div key={i} className="w-10 h-10 rounded-full border-2 border-white overflow-hidden">
              <img src={avatar} alt={`Kullanıcı ${i + 1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
        <p className="text-white font-semibold text-lg">
          Dünya çapında <span className="text-brand-10">1.000+</span> kişi tarafından güveniliyor
        </p>
      </div>
    </section>
  );
}

function BenefitsSectionTR() {
  const benefits = [
    {
      id: 'ai-companion',
      badge: 'Yapay Zekâ Destekli',
      title: 'Farkındalıklı Yapay Zekâ Yol Arkadaşı',
      description: 'Yapay zekâmız, düşüncelerinizi ve davranışlarınızı daha iyi anlamanıza yardımcı olmak için yanıtlarınızı analiz eder ve kişiselleştirilmiş içgörüler sunar.',
      features: [
        { icon: MessageCircle, label: 'Kişisel Gelişim' },
        { icon: Brain, label: 'Derin İçgörüler' },
      ],
      image: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=600',
      imageAlt: 'Yapay Zekâ Analiz Panosu',
      reverse: false,
    },
    {
      id: 'metrics',
      badge: 'Analitik',
      title: 'Kişilik Metrikleri',
      description: '12 temel özelliğinizi anlamaktan harmonik seviyelerinizi takip etmeye kadar, ihtiyaçlarınıza göre uyarlanmış nazik ve uygulanabilir öneriler alın.',
      features: [
        { icon: BarChart3, label: '12 Temel Özellik' },
        { icon: TrendingUp, label: 'Harmonik Seviyeler' },
      ],
      image: 'https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=600',
      imageAlt: 'Kişilik Metrikleri Grafiği',
      reverse: true,
    },
    {
      id: 'coach',
      badge: 'Destek',
      title: 'Koç Randevusu',
      description: 'Bazen öz değerlendirmeden daha fazlasına ihtiyaç duyarsınız. Sonuçlarınızı yorumlamanıza ve uygulanabilir gelişim planları oluşturmanıza yardımcı olabilecek sertifikalı koçlarla bağlantı kurun.',
      features: [
        { icon: Users, label: 'Uzman Rehberliği' },
        { icon: Sparkles, label: 'Kişiselleştirilmiş Planlar' },
      ],
      image: 'https://images.pexels.com/photos/5717546/pexels-photo-5717546.jpeg?auto=compress&cs=tinysrgb&w=600',
      imageAlt: 'Koç Oturumu',
      reverse: false,
    },
  ];

  return (
    <section id="features" className="py-20 md:py-28 px-4 md:px-8 bg-white">
      <div className="max-w-[1216px] mx-auto">
        <div className="text-center mb-16">
          <Badge variant="default" className="mb-4">Faydalar</Badge>
          <h2 className="text-3xl md:text-heading-md font-bold text-brand-90 mb-4">
            Kullanıcılarımızın en sevdiği gerçek yaşam faydalarını keşfedin.
          </h2>
          <p className="text-paragraph-xl text-gray-60 max-w-2xl mx-auto">
            NLP OS™, güçlü yönlerinizi derinlemesine keşfetmeniz, zorlukları yönetmeniz ve günde yalnızca birkaç dakikada netlik bulmanız için araçlar sunar.
          </p>
        </div>
        <div className="space-y-24">
          {benefits.map((benefit) => (
            <div key={benefit.id} className={`flex flex-col gap-12 items-center ${benefit.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'}`}>
              <div className="flex-1 w-full">
                <div className="relative">
                  <div className="absolute inset-0 bg-success-10 rounded-[32px] transform rotate-2" />
                  <div className="relative rounded-[32px] overflow-hidden shadow-soft-lg">
                    <img src={benefit.image} alt={benefit.imageAlt} className="w-full h-[300px] md:h-[400px] object-cover" />
                  </div>
                  <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-soft p-4 hidden md:flex items-center gap-3">
                    <div className="w-10 h-10 bg-success-10 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-success-40" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-80">Doğrulanmış İçgörüler</p>
                      <p className="text-xs text-gray-40">Bilimsel dayanaklı yöntemler</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-1 w-full">
                <Badge variant={benefit.id === 'ai-companion' ? 'teal' : benefit.id === 'metrics' ? 'orange' : 'default'} className="mb-4">
                  {benefit.badge}
                </Badge>
                <h3 className="text-2xl md:text-heading-sm font-bold text-brand-90 mb-4">{benefit.title}</h3>
                <p className="text-paragraph-lg text-gray-60 mb-6">{benefit.description}</p>
                <div className="flex flex-wrap gap-4">
                  {benefit.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 px-4 py-2 bg-gray-10 rounded-full">
                      <feature.icon className="w-4 h-4 text-success-40" />
                      <span className="text-sm font-medium text-gray-60">{feature.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ImpactSectionTR() {
  const stats = [
    { value: '%92', label: 'Öz Farkındalıkta Artış' },
    { value: '7,2x', label: 'Gelişim Oranı' },
    { value: '120 Bin', label: 'Tamamlanan Değerlendirme' },
  ];
  return (
    <section className="py-16 md:py-20 px-4 md:px-8 bg-success-40">
      <div className="max-w-[1216px] mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-heading-sm font-bold text-white mb-2">Şimdiye Kadarki Etkimiz</h2>
          <p className="text-success-10 text-paragraph-lg max-w-xl mx-auto">
            NLP OS™, binlerce kişinin gerçek potansiyelini keşfetmesine ve kişisel ile profesyonel yaşamlarında dönüştürücü sonuçlar elde etmesine yardımcı oluyor.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 md:gap-12">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <p className="text-5xl md:text-6xl font-bold text-white mb-2">{stat.value}</p>
              <p className="text-success-10 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSectionTR() {
  const features = [
    { icon: Brain, title: '7/24 Yapay Zekâ Kişilik Sohbeti', description: 'Kişilik özellikleriniz ve gelişim fırsatlarınız hakkında anlık içgörüler almak için yapay zekâ yol arkadaşımızla gündüz veya gece konuşun.', color: 'success' },
    { icon: MessageSquare, title: 'Günlük Gelişim Takibi', description: 'Kısa günlük kontroller, ilerlemenizin farkında kalmanıza ve kişisel gelişim yolculuğunuzda ivmenizi korumanıza yardımcı olur.', color: 'orange' },
    { icon: Lightbulb, title: 'Kişiselleştirilmiş İçgörüler ve İpuçları', description: 'Yeni beceriler geliştirmenize ve zorlukların üstesinden gelmenize yardımcı olmak için benzersiz profilinize göre hazırlanmış öneriler alın.', color: 'teal' },
    { icon: LineChart, title: 'İlerleme Analitiği', description: 'Görsel paneller, zaman içindeki gelişiminizi göstererek başarılarınızı kutlamanıza ve sürekli gelişim alanlarını belirlemenize yardımcı olur.', color: 'brand' },
  ];

  return (
    <section id="about" className="py-20 md:py-28 px-4 md:px-8 bg-brand-5">
      <div className="max-w-[1216px] mx-auto">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          <div className="lg:w-1/3">
            <Badge variant="default" className="mb-4">Özellikler</Badge>
            <h2 className="text-3xl md:text-heading-md font-bold text-brand-90 mb-4">Gelişiminiz İçin Tasarlanan Özellikler</h2>
            <p className="text-paragraph-lg text-gray-60">
              Daha sağlıklı alışkanlıklar ve daha güçlü bir zihniyet geliştirmek için kendini keşif ile rehberli farkındalığı birleştiren araçları keşfedin.
            </p>
          </div>
          <div className="lg:w-2/3 grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-3xl p-6 border border-gray-20 hover:shadow-soft-lg transition-shadow">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${feature.color === 'success' ? 'bg-success-10' : feature.color === 'orange' ? 'bg-orange-10' : feature.color === 'teal' ? 'bg-teal-10' : 'bg-brand-10'}`}>
                  <feature.icon className={`w-6 h-6 ${feature.color === 'success' ? 'text-success-40' : feature.color === 'orange' ? 'text-orange-50' : feature.color === 'teal' ? 'text-teal-50' : 'text-brand-60'}`} />
                </div>
                <h3 className="text-lg font-bold text-gray-80 mb-2">{feature.title}</h3>
                <p className="text-paragraph-sm text-gray-60">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSectionTR() {
  const faqs = [
    {
      question: 'Terapi yerine geçer mi?',
      answer: 'Hayır, NLP OS™ profesyonel terapi veya ruh sağlığı tedavisinin yerine geçmez. Kişilik özelliklerinizi, güçlü yönlerinizi ve gelişim alanlarınızı anlamanıza yardımcı olmak için tasarlanmış bir öz keşif aracıdır. Ruh sağlığı zorluğu yaşıyorsanız, lütfen nitelikli bir sağlık uzmanına danışın.',
    },
    {
      question: 'Değerlendirme ne kadar sürer?',
      answer: 'Tam NLP OS™ değerlendirmesi genellikle 15-20 dakika içinde tamamlanır. En doğru sonuçları almak için dikkatinizi dağıtmadan odaklanabileceğiniz sessiz bir zaman bulmanızı öneririz.',
    },
    {
      question: 'Kişisel sağlık verilerim güvende mi?',
      answer: 'Evet, veri gizliliği ve güvenliği en önemli önceliğimizdir. Endüstri standardında şifreleme kullanıyoruz ve kişisel bilgilerinizi üçüncü taraflarla asla paylaşmıyoruz. Değerlendirme sonuçlarınız güvenli bir şekilde saklanır ve yalnızca siz ile paylaşmayı seçtiğiniz koçlar tarafından erişilebilir.',
    },
    {
      question: "NLP OS™'u ücretsiz kullanabilir miyim?",
      answer: "Evet, temel kişilik profilinizi içeren ücretsiz bir değerlendirme deneyimi sunuyoruz. Ayrıntılı yapay zekâ analizi, koç bağlantıları ve ilerleme takibi gibi premium özellikler abonelik planlarımız aracılığıyla kullanılabilir.",
    },
    {
      question: "NLP OS™'u hangi cihazlarda kullanabilirim?",
      answer: 'NLP OS™, masaüstü bilgisayarlar, dizüstü bilgisayarlar, tabletler ve akıllı telefonlar dahil modern bir web tarayıcısı olan herhangi bir cihazda çalışan web tabanlı bir uygulamadır. İlerlemeniz tüm cihazlarınız arasında otomatik olarak senkronize edilir.',
    },
  ];

  return (
    <section id="faq" className="py-20 md:py-28 px-4 md:px-8 bg-white">
      <div className="max-w-[800px] mx-auto">
        <div className="text-center mb-12">
          <Badge variant="brand" className="mb-4">SSS</Badge>
          <h2 className="text-3xl md:text-heading-md font-bold text-brand-90 mb-4">Sıkça Sorulan Sorular</h2>
          <p className="text-paragraph-xl text-gray-60">Sorularınız mı var? Lütfen önce aşağıdaki bölüme göz atın.</p>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger className="text-left text-lg">{faq.question}</AccordionTrigger>
              <AccordionContent className="text-paragraph-md">{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function CTASectionTR() {
  const navigate = useNavigate();
  return (
    <section className="py-20 md:py-28 px-4 md:px-8 bg-brand-40">
      <div className="max-w-[1216px] mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="flex-1 relative hidden lg:block">
            <div className="relative max-w-sm mx-auto">
              <div className="absolute inset-0 bg-brand-60/30 rounded-[32px] transform -rotate-3" />
              <div className="relative bg-white rounded-[32px] shadow-soft-lg p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-success-10 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🎯</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-80">Gelişim Yolculuğunuz</p>
                    <p className="text-sm text-gray-40">Size özel olarak kişiselleştirildi</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-success-5 rounded-xl">
                    <div className="w-8 h-8 bg-success-40 rounded-full flex items-center justify-center text-white text-sm font-bold">1</div>
                    <span className="text-sm text-gray-60">Değerlendirmeyi Tamamla</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-10 rounded-xl">
                    <div className="w-8 h-8 bg-gray-30 rounded-full flex items-center justify-center text-gray-60 text-sm font-bold">2</div>
                    <span className="text-sm text-gray-40">Yapay Zekâ İçgörüleri Al</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-10 rounded-xl">
                    <div className="w-8 h-8 bg-gray-30 rounded-full flex items-center justify-center text-gray-60 text-sm font-bold">3</div>
                    <span className="text-sm text-gray-40">Koçla Bağlantı Kur</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 text-center lg:text-left">
            <h2 className="text-3xl md:text-heading-md font-bold text-white mb-4">Gelişiminize Özen Gösterin — Bugün Başlayın</h2>
            <p className="text-paragraph-xl text-brand-10 mb-8 max-w-xl mx-auto lg:mx-0">
              İlk adımı atmak için beklemeyin. Kendini keşif yolculuğunuz, şimdi her zaman ve her cihazda erişebileceğiniz basit, bilimsel dayanaklı bir değerlendirmeyle başlar.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button variant="secondary" size="lg" onClick={() => navigate('/tr/register')} className="gap-2 bg-white text-brand-90 hover:bg-gray-10">
                Şimdi Başla
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate('/tr/coach-register')} className="gap-2 border-white text-white hover:bg-white/10">
                Koç Ol
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FooterTR() {
  const navigate = useNavigate();
  const handleNavClick = (href: string) => {
    if (href.startsWith('#')) {
      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    } else if (href.startsWith('/')) {
      navigate(href);
    }
  };

  const navLinks = [
    { label: 'Ana Sayfa', href: '/tr' },
    { label: 'Hakkında', href: '#about' },
    { label: 'Özellikler', href: '#features' },
    { label: 'SSS', href: '#faq' },
  ];

  const socialLinks = [
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Facebook, href: '#', label: 'Facebook' },
  ];

  return (
    <footer className="py-16 md:py-24 px-4 md:px-8 bg-brand-5">
      <div className="max-w-[1216px] mx-auto">
        <div className="flex flex-col lg:flex-row justify-between gap-12 mb-12">
          <div className="max-w-md">
            <div className="flex items-center gap-2 mb-6">
              <img src="/human_potential_os_logo_V2_cropped.png" alt="NLP OS" className="h-14" />
            </div>
            <p className="text-paragraph-lg text-gray-60 mb-6">
              Gelişim odaklı bir zihniyet oluşturmak, kendinizi anlamakla başlar — NLP OS™ ise bu yolculuğun her adımında size rehberlik etmek için burada.
            </p>
            <nav className="flex flex-wrap gap-6">
              {navLinks.map((link) => (
                <button key={link.label} onClick={() => handleNavClick(link.href)} className="text-md font-semibold text-gray-60 hover:text-brand-60 transition-colors">
                  {link.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
        <Separator className="bg-gray-20" />
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 pt-8">
          <p className="text-sm text-gray-40">Telif Hakkı 2025 NLP OS™. Tüm Hakları Saklıdır.</p>
          <div className="flex items-center gap-4">
            {socialLinks.map((social) => (
              <a key={social.label} href={social.href} className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-10 text-gray-60 hover:bg-brand-10 hover:text-brand-60 transition-colors" aria-label={social.label}>
                <social.icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

export function HomePageTR() {
  return (
    <div className="min-h-screen bg-brand-5 font-urbanist">
      <HeaderTR />
      <main>
        <HeroSectionTR />
        <TrustBarTR />
        <BenefitsSectionTR />
        <ImpactSectionTR />
        <FeaturesSectionTR />
        <FAQSectionTR />
        <CTASectionTR />
      </main>
      <FooterTR />
    </div>
  );
}
